import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import { CVDocumentPreview } from "../../../components/cv/CVDocumentPreview";
import { useCVData } from "../../../lib/cv-mock-data";
import { CvShareBottomSheet } from "../../../components/cv/CvShareBottomSheet";
import { CVBottomBar } from "../../../components/cv/CVBottomBar";
import { CustomizeBottomSheet } from "../../../components/cv/CustomizeBottomSheet";
import { WhereHeardPrompt } from "../../../components/cv/WhereHeardPrompt";
import { WebPageHeader } from "../../../components/common/WebPageHeader";
import { NovaSparkle } from "../../../components/common/NovaSparkle";
import { MaterialSymbol } from "../../../components/icons/MaterialSymbol";
import { useAuth } from "../../../hooks/useAuth";
import { buildCvRoute, buildTailoredCvCreateRoute, leaveCvEditor } from "../../../lib/cv-target-route";
import { usePublishPageContext } from "../../../lib/page-context";
import { buildPublicCvRouteUrl } from "../../../lib/public-web";

type CvImportNotice = "updated" | "expired" | "failed";

function normalizeResumeImportNotice(value?: string | string[]) {
  const raw = Array.isArray(value) ? value[0] : value;

  if (raw === "updated" || raw === "expired" || raw === "failed") {
    return raw;
  }

  return null;
}

function HeaderShareButton({
  icon,
  label,
  onClick,
}: {
  icon: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className="tabbio-chip"
      onClick={onClick}
      style={{ width: 40, height: 40, padding: 0, justifyContent: "center" }}
    >
      <MaterialSymbol name={icon} size={18} />
    </button>
  );
}

function ResumeImportNotice({
  notice,
  onCreateTailored,
  onDismiss,
  onReview,
  onUploadAnother,
}: {
  notice: CvImportNotice;
  onCreateTailored: () => void;
  onDismiss: () => void;
  onReview: () => void;
  onUploadAnother: () => void;
}) {
  const { t } = useTranslation("seeker");
  const titleKey =
    notice === "updated"
      ? "cv.resumeImportNotice.updatedTitle"
      : notice === "expired"
        ? "cv.resumeImportNotice.expiredTitle"
        : "cv.resumeImportNotice.failedTitle";
  const bodyKey =
    notice === "updated"
      ? "cv.resumeImportNotice.updatedBody"
      : notice === "expired"
        ? "cv.resumeImportNotice.expiredBody"
        : "cv.resumeImportNotice.failedBody";

  return (
    <section
      style={{
        display: "grid",
        gridTemplateColumns: "auto minmax(0, 1fr) auto",
        gap: 16,
        alignItems: "flex-start",
        padding: 20,
        marginBottom: 24,
        borderRadius: 20,
        background: "var(--menu-bg)",
        boxShadow: "var(--gem-input-shadow)",
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
      }}
    >
      <div
        aria-hidden
        style={{
          width: 28,
          height: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--text-secondary)",
        }}
      >
        {notice === "updated" ? (
          <NovaSparkle size={22} />
        ) : (
          <MaterialSymbol name={notice === "expired" ? "schedule" : "error_outline"} size={22} />
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 15,
            fontWeight: 500,
            lineHeight: "20px",
            color: "var(--text-primary)",
          }}
        >
          {t(titleKey)}
        </div>
        <p
          style={{
            margin: "4px 0 0",
            fontSize: 13,
            lineHeight: "19px",
            color: "var(--text-secondary)",
            maxWidth: 720,
          }}
        >
          {t(bodyKey)}
        </p>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginTop: 14,
          }}
        >
          <button
            type="button"
            onClick={onCreateTailored}
            className="tabbio-chip"
          >
            <NovaSparkle size={16} />
            {t("cv.resumeImportNotice.createTailored")}
          </button>
          <button type="button" onClick={onReview} className="tabbio-chip">
            <MaterialSymbol name="edit" size={18} />
            {t("cv.resumeImportNotice.reviewCv")}
          </button>
          <button type="button" onClick={onUploadAnother} className="tabbio-chip">
            <MaterialSymbol name="open_in_new" size={18} />
            {t("cv.resumeImportNotice.uploadAnother")}
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t("cv.resumeImportNotice.dismiss")}
        style={{
          width: 32,
          height: 32,
          border: "none",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          background: "transparent",
          color: "var(--text-secondary)",
          transition: "background-color 150ms",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "var(--hover-strong)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
        }}
      >
        <MaterialSymbol name="close" size={18} />
      </button>
    </section>
  );
}


export default function CVWebScreen({
  initialCustomizeOpen = false,
}: {
  initialCustomizeOpen?: boolean;
} = {}) {
  const { t } = useTranslation("seeker");
  const router = useRouter();
  const params = useLocalSearchParams<{
    share?: string | string[];
    resumeImport?: string;
    resumeImportSkillId?: string;
  }>();
  const { profile, target, isDelegated, sectionsConfig } = useCVData();
  const { isUaePassVerified } = useAuth();
  const [showShare, setShowShare] = useState(false);
  const shareFromLink =
    (Array.isArray(params.share) ? params.share[0] : params.share) === "1";
  const [showCustomize, setShowCustomize] = useState(initialCustomizeOpen);
  const resumeImportNotice = normalizeResumeImportNotice(params.resumeImport);
  const isDelegatedTarget = isDelegated;

  // Tell the agent chat column which CV the panel is showing. `target` is the
  // resolved CV id when the route displays a specific CV (delegated grant or
  // the auto-apply primary); otherwise "master". Preserve an explicit profile
  // selection in state: page-context normalizes away ?cvTarget=master, and
  // legacy /cv clients use the same entity id for the semantic Main selection.
  usePublishPageContext(
    useMemo(
      () => ({
        screen: "cv" as const,
        entity: {
          type: "cv" as const,
          id: target && target !== "master" ? target : "master",
        },
        ...(target === "master" ? { state: { cvTarget: "master" } } : {}),
      }),
      [target],
    ),
  );

  // SCRUM-355: reset scroll to the top on entry and when a resume-import
  // notice first appears (null → "updated" etc.). Skip the dismiss transition
  // (notice → null): that would jump the page to top out from under the
  // user when they click Dismiss while scrolled mid-CV.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const previousNoticeRef = useRef<CvImportNotice | null>(null);
  useEffect(() => {
    const wasAbsent = previousNoticeRef.current === null;
    previousNoticeRef.current = resumeImportNotice;
    // On first mount: wasAbsent is true and resumeImportNotice may be null,
    // so the second condition also gates the mount reset to fire (notice
    // null → null transition is the mount case).
    if (wasAbsent) {
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [resumeImportNotice]);

  const shareUrl = profile.username ? buildPublicCvRouteUrl(profile.username) : null;
  const shareUrlLabel = shareUrl
    ? shareUrl.replace(/^https?:\/\//, '')
    : t('cv.share.linkUnavailable', {
        defaultValue: 'Set your Tabbio link to share',
      });

  const navigateToEdit = (screen: string) => {
    router.push(buildCvRoute(`/(app)/cv/${screen}`, target));
  };

  const handleUploadAnother = () => {
    router.push(buildCvRoute("/(app)/cv/import-resume", target));
  };

  const closeCustomize = () => {
    if (initialCustomizeOpen) {
      router.replace(buildCvRoute("/(app)/cv", target));
      return;
    }
    setShowCustomize(false);
  };

  return (
    <div
      className="tabbio-phone-cv-page"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "var(--bg)",
      }}
    >
      <div
        ref={scrollContainerRef}
        style={{
          flex: 1,
          overflowY: "auto",
          paddingBottom: 128,
          boxSizing: "border-box",
            paddingTop: 30
        }}
      >
        <CVDocumentPreview
          onEditSection={navigateToEdit}
          sectionsConfig={sectionsConfig}
          isUaePassVerified={isUaePassVerified}
          enableVerification
          topSlot={
            // The CV document canvas has no page padding of its own, so the
            // header would sit flush against the panel edges. Inset it to the
            // same margin the rest of the app uses.
            <div className="tabbio-page-header-inset">
              <WebPageHeader
                title={t("cv.previewScreen.title", "CV")}
                subtitle={shareUrlLabel}
                onBack={() => {
                  leaveCvEditor(router);
                }}
              >
                <HeaderShareButton
                  icon="ios_share"
                  label={t("cv.previewScreen.shareWebCv")}
                  onClick={() => setShowShare(true)}
                />
              </WebPageHeader>
              {!isDelegatedTarget && resumeImportNotice ? (
                <ResumeImportNotice
                  notice={resumeImportNotice}
                  onCreateTailored={() =>
                    router.push(
                      buildTailoredCvCreateRoute(undefined, {
                        skillId:
                          typeof params.resumeImportSkillId === "string"
                            ? params.resumeImportSkillId
                            : null,
                      }),
                    )
                  }
                  onDismiss={() => router.replace(buildCvRoute("/(app)/cv", target))}
                  onReview={() =>
                    router.push(buildCvRoute("/(app)/cv/edit-sections", target))
                  }
                  onUploadAnother={handleUploadAnother}
                />
              ) : null}
            </div>
          }
        />
      </div>

      <div
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 128,
          zIndex: 40,
          pointerEvents: "none",
          background: "linear-gradient(to bottom, transparent 0%, var(--bg) 76%)",
        }}
      />
      <CVBottomBar
        target={target}
        placement="content"
        onCustomizePress={() => setShowCustomize(true)}
      />

      {showCustomize ? (
        <CustomizeBottomSheet onClose={closeCustomize} />
      ) : null}

      {/* "Where did you hear about us?": once per account, only when this CV
          was opened from My Tabbio (the hub stamps the request). */}
      {!isDelegatedTarget ? <WhereHeardPrompt /> : null}

      {showShare || shareFromLink ? (
        <CvShareBottomSheet
          visible={showShare || shareFromLink}
          publicUrl={shareUrl}
          onClose={() => {
            setShowShare(false);
            if (shareFromLink) router.setParams({ share: undefined });
          }}
        />
      ) : null}
    </div>
  );
}
